import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import Modal from '@atlaskit/modal-dialog';
import _ from 'lodash';
import { DateTime } from 'luxon';

import withoutNulls from 'api/search/withoutNulls';
import updateDashboard from 'api/updateDashboard';
import Row from 'components/Common/Row';
import FilterPlates from 'components/FilterPlates';
import { getCompositeMetricIds } from 'components/Studio/compositeUtils';
import getMetricDataTypes from 'components/Studio/getMetricDataTypes';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DashboardContext from 'contexts/DashboardContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import ReportContext from 'contexts/ReportContext';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import STORE from 'store';

// The Composite with its own filters, leaving them out when there are none
const withFilters = ({
  composite,
  filters,
}: {
  composite: Studio.CompositeContent;
  filters: FilterPlateType[];
}): Studio.CompositeContent => ({
  ..._.omit(composite, 'filters'),
  ...(filters.length > 0 ? { filters } : {}),
});

// Writes the Composite back into the dashboard's or report's canvas, the
// only place it's kept (it has no gadget doc)
const useSaveComposite = (cardId: string) => {
  const { dashboard } = useContext(DashboardContext);
  const { report } = useContext(ReportContext);
  const { selectedAccountId, accountRef } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);

  return useCallback(
    async (composite: Studio.CompositeContent) => {
      const replaceCard = (canvas: Canvas): Canvas => ({
        ...canvas,
        cards: canvas.cards.map((c) =>
          c.layout.i === cardId && c.content.type === 'Studio Composite'
            ? { ...c, content: { ...c.content, composite } }
            : c,
        ),
      });
      const stamp = {
        updatedBy: currentUserId,
        updatedOn: DateTime.utc().toISO() ?? '',
      };
      if (report) {
        await STORE.getReportsRef({ accountId: selectedAccountId })
          .doc(report.id)
          .set(
            withoutNulls({
              ...report,
              canvas: replaceCard(report.canvas),
              ...stamp,
            }),
          );
        return;
      }
      if (dashboard) {
        await updateDashboard({
          id: dashboard.id,
          newDashboard: {
            ...dashboard,
            canvas: replaceCard(dashboard.canvas),
            ...stamp,
          },
          accountRef,
        });
      }
    },
    [accountRef, cardId, currentUserId, dashboard, report, selectedAccountId],
  );
};

// A Composite's own filters, set from a dashboard or report as Studio sets
// them (its elements' own stay as they are). Its dates for the view are on
// its date overlay; its saved dates are set in Studio.
const CompositeFiltersModal = ({
  card,
  close,
}: {
  card: CanvasCard.StudioCompositeCard;
  close: () => void;
}) => {
  const { composite } = card.content;
  const { metricOptionsLookup } = useContext(MetricOptionsContext);

  const [filters, setFilters] = useState<FilterPlateType[]>(
    composite.filters ?? [],
  );
  const [isSaving, setIsSaving] = useState(false);

  // Its metrics' datasets: what its dates and filters can be on
  const dataTypes = useMemo(
    () =>
      getMetricDataTypes({
        metricIds: getCompositeMetricIds(composite),
        metricOptionsLookup,
      }),
    [composite, metricOptionsLookup],
  );

  // The filter form may pass an updater, so resolve it against the latest
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const setScope = useCallback(
    (action: React.SetStateAction<FilterPlateType[]>) =>
      setFilters(
        typeof action === 'function' ? action(filtersRef.current) : action,
      ),
    [],
  );

  const saveComposite = useSaveComposite(card.layout.i);
  const onSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveComposite(withFilters({ composite, filters }));
      close();
    } finally {
      setIsSaving(false);
    }
  }, [close, composite, filters, saveComposite]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader
          title={`Filters · ${composite.name || 'Untitled'}`}
          onClose={close}
        />
        <FormContent>
          <div data-testid="composite-filters">
            {dataTypes.length === 0 ? (
              <Typography.Body
                type="Body 12"
                color="var(--color-text-tertiary)"
              >
                Add a metric to choose filters.
              </Typography.Body>
            ) : (
              <FilterPlatesProvider
                scope={filters}
                setScope={setScope}
                dataTypes={dataTypes}
              >
                <FilterPlates
                  isScope
                  testIdPrefix="composite"
                  label="Add filter"
                />
              </FilterPlatesProvider>
            )}
          </div>
        </FormContent>
        <Row spaceBetween>
          <Button
            size="Small"
            type="Secondary"
            label="Cancel"
            onClick={close}
            isDisabled={isSaving}
          />
          <Button
            size="Small"
            type="Primary"
            label="Save"
            onClick={onSave}
            isLoading={isSaving}
            isDisabled={isSaving}
            testId="composite-filters-save"
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default CompositeFiltersModal;
